import { useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { ListChecks, Shapes } from 'lucide-react';
import StepPrompt from './StepPrompt';
import { widgetRefsForStep } from '../widgetForStep';
import type { ActivityStep, WidgetRefConfig } from '../classroomApi';

// /class live redesign (2026-09-11, the instructor verbatim: "its not very
// intuitive and the focus should be on the question and we may need to use
// tab design to unclutter so user can see active question and choices").
//
// Two decisions this component encodes, both aimed at a 390 px phone held by
// one of 77 students in a live room:
//
// 1. **The question IS the page.** The prompt is the card's <h2> at
//    text-lg/xl, not a text-sm line above a widget. Everything else on the
//    live card is one line of meta above it or the answer control below it.
//
// 2. **Tabs only where there is genuinely parallel content**, which here
//    means: the activity carries a lab widget the student is meant to LOOK
//    at while answering. 43 of the catalogue's 82 activities carry
//    `widget_refs`, and a widget is tall — on a phone it pushed the options
//    off the bottom of the screen, which is exactly the "can't see the
//    active question and choices" complaint. So the widget and the answer
//    control take turns in one tab strip while **the prompt stays above the
//    strip, visible in both tabs**. Tabs that could hide the question were
//    never on the table; the question is the one thing that must never move.
//
//    Where there is no widget (the other 39 activities, and every poll whose
//    options are plain text) there is no second view worth a tab, so no tab
//    strip renders at all — the question and its choices are simply the card.
//
//    A step whose ANSWER is the widget (`answer: 'widget'`, WIP-016) is
//    never tabbed: the widget and its Submit button are one control and have
//    to be on screen together.
//
// Both panels stay MOUNTED (`hidden`, not unmounted) so switching tabs never
// resets a widget a student has already been fiddling with — and, for the
// widget-answer bridge in StepPrompt, never orphans a captured snapshot
// behind a widget that has silently reset itself.

const TABS = [
  { id: 'answer', label: 'Answer', Icon: ListChecks },
  { id: 'model', label: 'Model', Icon: Shapes },
] as const;

type TabId = (typeof TABS)[number]['id'];

interface LiveQuestionProps {
  step: ActivityStep;
  widgetRefs: WidgetRefConfig[];
  /** The answer control. Absent on instruct/group steps (nothing to submit),
   *  which is also what suppresses the tab strip — a tab called "Answer"
   *  with nothing in it is worse than no tabs. */
  children?: ReactNode;
}

export default function LiveQuestion({ step, widgetRefs, children }: LiveQuestionProps) {
  const [active, setActive] = useState<TabId>('answer');
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const isWidgetAnswer = step.answer === 'widget';
  // A step's own widget wins over the activity's (widgetForStep.ts — the card
  // sort that showed card 1's model on card 16). Resolved HERE as well as
  // inside StepPrompt because it is what decides whether there is a second
  // view to tab to at all: reading `activity.widget_refs` alone would leave a
  // step-level widget with no tab to live in, and therefore unrendered.
  const refs = widgetRefsForStep(step, widgetRefs);
  const hasWidgets = refs.length > 0;
  const useTabs = hasWidgets && !isWidgetAnswer && !!children;

  // StepPrompt owns widget rendering (and the widget-answer state bridge), so
  // it is still the only thing that mounts a WidgetRenderer here. It also
  // renders the prompt itself, which this component has just rendered as the
  // card's heading — hence the blank prompt: the same text twice, once large
  // and once small, is worse than an empty <p> that generates no line box.
  // The tidy version of this is a `hidePrompt` prop on StepPrompt; that file
  // is owned by concurrent work this session was told not to touch, so it is
  // written up in the report instead of edited here.
  const widgetBlock = hasWidgets ? (
    <StepPrompt step={{ ...step, prompt: '' }} widgetRefs={refs} />
  ) : null;

  const prompt = (
    <h2 className="text-lg font-semibold leading-snug text-ws-ink sm:text-xl">{step.prompt}</h2>
  );

  if (!useTabs) {
    return (
      <div className="space-y-4">
        {prompt}
        {widgetBlock}
        {children}
      </div>
    );
  }

  // WAI-ARIA APG tabs, manual activation — same pattern (roving tabindex,
  // arrow/Home/End keys, aria-selected, aria-controls) as the run sheet's
  // ActivityTabs, so the two tab strips in this feature behave identically.
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (idx + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TABS.length - 1;
    if (next === null) return;
    e.preventDefault();
    const id = TABS[next].id;
    setActive(id);
    tabRefs.current[id]?.focus();
  };

  return (
    <div className="space-y-4">
      {prompt}

      <div role="tablist" aria-label="Question view" className="flex gap-2">
        {TABS.map((tab, idx) => {
          const selected = tab.id === active;
          return (
            <button
              key={tab.id}
              ref={(el) => { tabRefs.current[tab.id] = el; }}
              type="button"
              role="tab"
              id={`live-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`live-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(tab.id)}
              onKeyDown={(e) => onKeyDown(e, idx)}
              className={`inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg border text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent ${
                selected
                  ? 'border-ws-accent bg-ws-accent text-ws-on-accent'
                  : 'border-ws-border bg-ws-surface text-ws-ink-2 hover:bg-ws-surface-2'
              }`}
            >
              <tab.Icon size={16} aria-hidden="true" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id="live-panel-answer" aria-labelledby="live-tab-answer" hidden={active !== 'answer'}>
        {children}
      </div>
      <div role="tabpanel" id="live-panel-model" aria-labelledby="live-tab-model" hidden={active !== 'model'}>
        {widgetBlock}
      </div>
    </div>
  );
}
